// Runtime read-side for the F4 → OBJECTS calibration tool (ui/objectLab.ts). That tool lets the owner
// resize a buildable GLB or a dropped-item icon sprite against a real foundation/wall/player rig and
// SAVE the chosen scale into one of these two flat JSON files (served as static assets, written by the
// dev-only /__save_resizer endpoint in vite.config.ts -- see objectLab.ts). Game code calls buildableScale
// / itemScale wherever it used to hardcode a scale constant, so a resize the owner dials in takes effect
// without a code change or redeploy (just edit-and-reload in dev; the committed JSON ships with prod).
//
// Fetched once at module load -- both files are a few hundred bytes, so in practice they resolve within a
// handful of ms. A missing file (fresh clone, nothing saved yet) or a 404 just means "no overrides", not
// an error. buildableScale/itemScale below are readable immediately (fall back until the fetch lands), for
// any caller that only needs a best-effort synchronous read; `resizersReady` (below) is for callers that
// must NOT compute a scale before the real saved value is in -- see gltf.ts for why that matters.

const BUILDABLE_URL = '/data/buildable_resizers.json';
const ITEM_URL = '/data/item_resizers.json';

let buildableMap: Record<string, number> = {};
let itemMap: Record<string, number> = {};

async function load(url: string): Promise<Record<string, number>> {
  try {
    const res = await fetch(url);
    if (!res.ok) return {};
    return (await res.json()) as Record<string, number>;
  } catch { return {}; }
}

// Kick off both fetches once. Exported so a caller can force/await a re-check (e.g. ObjectLab after a
// save), but also self-primes at module load -- dropped.ts and craftables.ts import buildableScale/
// itemScale directly, so simply importing this module (transitively, at game boot) is enough; no
// separate wiring needed in main.ts.
let preloaded = false;
let resolveReady: () => void;
// Resolves once BOTH resizer files have loaded (or failed) -- gltf.ts awaits this before fitting the FIRST
// load of any GLB in a session, so a buildable's scale is never permanently fit at the fallback because its
// model happened to finish parsing a few ms before this tiny JSON fetch did (real bug: #showcase builds
// every buildable at boot, racing the two -- the furnace/large_furnace lost that race and stuck at scale 1
// despite a saved 0.75/2.135). Cheap: this JSON is tiny, so the extra wait is imperceptible in practice.
export const resizersReady: Promise<void> = new Promise((res) => { resolveReady = res; });
export function preloadResizers(): void {
  if (preloaded) return;
  preloaded = true;
  Promise.all([load(BUILDABLE_URL), load(ITEM_URL)]).then(([b, i]) => { buildableMap = b; itemMap = i; resolveReady(); });
}
preloadResizers();

// A buildable GLB's saved scale (keyed by its filename under public/models/, e.g. 'rainwater_catcher_v2.glb'),
// or `fallback` if nothing has been saved for it yet.
export function buildableScale(file: string, fallback: number): number {
  return buildableMap[file] ?? fallback;
}

// A dropped item's saved icon-sprite scale (keyed by ItemId), or `fallback`.
export function itemScale(id: string, fallback: number): number {
  return itemMap[id] ?? fallback;
}
